import { useEffect, useState } from 'react';
import { Dropdown, Form, Popup, Icon } from 'semantic-ui-react';
import { useTeamQuery } from '~/requests/team';
import { useUserContext } from '~/hooks';
import { updateTeamCompany } from '~/api/team';
import { getCompany, patchCompany } from '~/api/company';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { COMPANY_SUBTYPE_CODES, COMPANY_SUBTYPE_NAMES, CompanySubtypeCode } from '~/types/company';

import './CompanySubtypeTagsForm.scss';

type TeamSource = { source: 'team' };

export type CompanyProfileSubtypeSource = {
  source: 'companyProfile';
  companyId: string;
  subtypeCodes: CompanySubtypeCode[];
  onCompanyUpdated: (company: unknown) => void;
};

export type CompanySubtypeTagsFormProps = TeamSource | CompanyProfileSubtypeSource;

/**
 * IPP / EPC / DEV multi-select. On /settings/company uses PUT /team; on company profile Settings uses PATCH /company/:id.
 */
export function CompanySubtypeTagsForm(props: CompanySubtypeTagsFormProps) {
  const { loggedInUser } = useUserContext();
  const { data: team, refetch: refetchTeam } = useTeamQuery();
  const queryClient = useQueryClient();

  const initialCodes =
    props.source === 'team'
      ? (team?.subtypeCodes ?? [])
      : (props.subtypeCodes ?? []);

  const [localValue, setLocalValue] = useState<CompanySubtypeCode[]>(initialCodes);

  const profileSubtypeKey =
    props.source === 'companyProfile'
      ? (props.subtypeCodes ?? []).slice().sort().join(',')
      : '';

  useEffect(() => {
    if (props.source === 'team' && team?.subtypeCodes) {
      setLocalValue(team.subtypeCodes);
    }
  }, [props.source, team?.subtypeCodes]);

  useEffect(() => {
    if (props.source === 'companyProfile') {
      setLocalValue(props.subtypeCodes ?? []);
    }
  }, [props.source, profileSubtypeKey]);

  const { mutateAsync: updateSubtypes, status } = useMutation({
    mutationFn: async (codes: CompanySubtypeCode[]) => {
      if (props.source === 'team') {
        await updateTeamCompany({ subtypeCodes: codes });
        await refetchTeam();
        queryClient.invalidateQueries('team');
      } else {
        await patchCompany(props.companyId, { subtypeCodes: codes });
        const fresh = await getCompany(props.companyId);
        props.onCompanyUpdated(fresh);
      }
    },
    onError: (error: any) => {
      toast.error(error?.message ?? 'Failed to update company tags');
    },
    onSuccess: () => {
      toast.success('Company tags updated');
    },
  });

  const handleChange = async (_: unknown, data: { value?: unknown }) => {
    const codes = (Array.isArray(data.value) ? data.value : []) as CompanySubtypeCode[];
    setLocalValue(codes);
    try {
      await updateSubtypes(codes);
    } catch {
      if (props.source === 'team') {
        setLocalValue(team?.subtypeCodes ?? []);
      } else {
        setLocalValue(props.subtypeCodes ?? []);
      }
    }
  };

  const value =
    localValue ??
    (props.source === 'team' ? team?.subtypeCodes : props.subtypeCodes) ??
    [];
  const loading = status === 'loading';

  if (props.source === 'team' && loggedInUser.isAdmin) {
    return null;
  }

  return (
    <Form className="company-subtype-tags-form">
      <div className="company-subtype-tags-field">
        <label className="company-subtype-tags-label" htmlFor="company-subtype-tags-dropdown">
          Company type (select all that apply)
          <Popup
            inverted
            trigger={<Icon name="info circle" style={{ color: '#005b8e', marginLeft: '4px' }} tabIndex={0} />}
            content={
              <div>
                <strong style={{ fontSize: '16px' }}>Company Type Definitions</strong>
                <p style={{ color: '#FFFFFF', margin: '1em 0em', fontSize: '16px' }}>
                  Here’s how Conductor defines these roles:
                </p>
                <ul style={{ color: '#FFFFFF', margin: '1em 0em', paddingLeft: '1.2em' }}>
                  <li>
                    <strong>EPC:</strong> Engineering, Procurement, and Construction - a company contracting to build the energy system
                  </li>
                  <li>
                    <strong>DEV:</strong> Developer - a company advancing the project from site selection through utility interconnection and permitting
                  </li>
                  <li>
                    <strong>IPP:</strong> Independent Power Producer - a company owning and operating the energy system once it is built
                  </li>
                </ul>
              </div>
            }
            position="top center"
            wide="very"
            on={['hover', 'focus']}
          />
        </label>
        <div className="company-subtype-tags-dropdown-wrap">
          <Dropdown
            id="company-subtype-tags-dropdown"
            multiple
            selection
            className="company-subtype-tags-dropdown"
            options={COMPANY_SUBTYPE_CODES.map((code) => ({
              key: code,
              text: COMPANY_SUBTYPE_NAMES[code],
              value: code,
            }))}
            value={value}
            onChange={handleChange}
            placeholder="Select type(s)"
            disabled={loading}
            loading={loading}
          />
        </div>
      </div>
    </Form>
  );
}

export default CompanySubtypeTagsForm;
